import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { weatherAPI, farmAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Sun, Cloud, CloudRain, CloudSnow, Wind, Droplets, Thermometer,
  AlertTriangle, CheckCircle, RefreshCw, Loader2, MapPin, Calendar
} from 'lucide-react';
import {
  LAGOS_DEFAULT,
  buildPickerOptions,
  getSavedWeatherLocation,
  writeSavedChoice,
  locationFromPlot,
  resolveChoice,
} from '@/lib/weatherLocation';

const WeatherIcon = ({ condition }) => {
  const icons = {
    sunny: Sun,
    clear: Sun,
    cloudy: Cloud,
    rain: CloudRain,
    rainy: CloudRain,
    storm: CloudRain,
    snow: CloudSnow,
  };
  const Icon = icons[condition?.toLowerCase()] || Cloud;
  return <Icon className="w-full h-full" />;
};

export default function WeatherPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [plots, setPlots] = useState([]);
  // Starts at Lagos so the first paint has a stable name/coords
  // before plots load — resolved to the saved/default pick as soon
  // as the plot list arrives.
  const [location, setLocation] = useState({ ...LAGOS_DEFAULT });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [geoError, setGeoError] = useState(false);

  // Load the user's plots so the picker can offer them and the
  // default-resolution fallback can pick the first one.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await farmAPI.getPlots(user?.id);
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        setPlots(rows);
        setLocation(getSavedWeatherLocation(user, rows));
      } catch {
        if (cancelled) return;
        setLocation(getSavedWeatherLocation(user, []));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    loadWeather();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.lat, location.lon]);

  const pickerOptions = useMemo(() => buildPickerOptions(plots), [plots]);

  const loadWeather = async () => {
    setLoading(true);
    setError(false);
    try {
      const [weatherRes, forecastRes] = await Promise.all([
        weatherAPI.getWeather(location.lat, location.lon),
        weatherAPI.getForecast(location.lat, location.lon, 7),
      ]);
      setWeather(weatherRes.data);
      setForecast(forecastRes.data?.forecast || []);
    } catch (err) {
      // Backend now returns 503 on upstream failure rather than
      // inventing a 25°C / 60% rain fallback. Surface that honestly;
      // the retry button re-fires loadWeather.
      console.error('Failed to load weather', err);
      setWeather(null);
      setForecast([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const selectOption = (opt) => {
    setPickerOpen(false);
    setGeoError(false);
    if (opt.kind === 'geo') {
      if (!navigator.geolocation) {
        setGeoError(true);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocation({
            lat: pos.coords.latitude,
            lon: pos.coords.longitude,
            name: t('weather.currentLocation', { defaultValue: 'My location' }),
            kind: 'geo',
          });
          // Not persisted — see weatherLocation.js
        },
        () => setGeoError(true),
      );
      return;
    }
    if (opt.kind === 'plot') {
      const plot = plots.find((p) => p?.id === opt.plotId);
      const loc = locationFromPlot(plot);
      if (loc) {
        setLocation(loc);
        writeSavedChoice(user, { kind: 'plot', plotId: opt.plotId });
      }
      return;
    }
    if (opt.kind === 'state') {
      const choice = { kind: 'state', state: opt.state };
      const resolved = resolveChoice(choice, plots);
      if (resolved) {
        setLocation(resolved);
        writeSavedChoice(user, choice);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-farm-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 animate-fadeIn">
      <div className="flex items-start justify-between mb-6 gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl md:text-3xl font-bold">{t('weather.title')}</h1>
          <button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            className="mt-1 inline-flex items-center gap-1.5 text-muted-foreground hover:text-farm-primary focus:outline-none focus:ring-2 focus:ring-farm-primary rounded-md px-1 -mx-1"
            aria-expanded={pickerOpen}
            aria-haspopup="listbox"
            data-testid="weather-location-trigger"
          >
            <MapPin className="w-4 h-4" />
            <span className="font-medium">{location.name}</span>
            <span className="text-xs text-farm-primary">
              {t('weather.picker.change', { defaultValue: 'Change' })}
            </span>
          </button>
          {geoError && (
            <p className="text-xs text-amber-700 mt-1" data-testid="weather-geo-error">
              {t('weather.picker.geoError', {
                defaultValue: "Couldn't read your location. Pick a plot or state instead.",
              })}
            </p>
          )}
        </div>
        <Button variant="outline" onClick={loadWeather} aria-label={t('weather.refresh', { defaultValue: 'Refresh' })}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {pickerOpen && (
        <Card className="mb-6 border border-farm-primary/20" data-testid="weather-location-picker">
          <CardContent className="p-0 max-h-80 overflow-y-auto">
            <ul role="listbox" aria-label={t('weather.picker.label', { defaultValue: 'Weather location' })}>
              {pickerOptions.map((opt) => {
                const isSelected =
                  (opt.kind === 'plot' && location.kind === 'plot' && location.plotId === opt.plotId) ||
                  (opt.kind === 'state' && location.kind === 'state' && location.state === opt.state) ||
                  (opt.kind === 'geo' && location.kind === 'geo');
                return (
                  <li key={opt.id} role="option" aria-selected={isSelected}>
                    <button
                      type="button"
                      onClick={() => selectOption(opt)}
                      className={`w-full flex items-center gap-3 p-3 text-left hover:bg-farm-cream focus:outline-none focus:bg-farm-cream border-b border-gray-100 last:border-b-0 ${isSelected ? 'bg-farm-primary/5 font-semibold' : ''}`}
                      data-testid={`weather-picker-opt-${opt.id}`}
                    >
                      <MapPin className="w-4 h-4 text-farm-primary flex-shrink-0" />
                      <span className="flex-1 min-w-0 truncate text-sm">
                        {opt.labelKey
                          ? t(opt.labelKey, { defaultValue: 'My location' })
                          : opt.label}
                      </span>
                      {opt.kind === 'plot' && (
                        <span className="text-[10px] uppercase tracking-wider text-farm-forest/50">
                          {t('weather.picker.plotTag', { defaultValue: 'Plot' })}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      {error && !weather && (
        <Card
          className="border border-amber-300 bg-amber-50 mb-6"
          data-testid="weather-unavailable"
        >
          <CardContent className="p-6 flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900">
                {t('weather.error.title', { defaultValue: 'Weather unavailable' })}
              </h3>
              <p className="text-sm text-amber-800 mt-1">
                {t('weather.error.body', {
                  defaultValue:
                    "We couldn't reach the weather service. Please try again.",
                })}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={loadWeather}
                className="mt-3"
                data-testid="weather-retry"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('weather.error.retry', { defaultValue: 'Try again' })}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {weather && (
        <>
          {/* Current Weather */}
          <Card className="bg-gradient-to-br from-farm-primary to-farm-primary-dark text-white border-0 mb-6">
            <CardContent className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-center gap-6">
                  <div className="w-24 h-24 text-farm-secondary">
                    <WeatherIcon condition={weather.condition} />
                  </div>
                  <div>
                    <p className="text-white/80 text-sm">{t('weather.currentWeather')}</p>
                    <h2 className="text-5xl font-bold">{weather.temperature}°C</h2>
                    <p className="text-white/90 text-lg mt-1 capitalize">{weather.description}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/10 rounded-xl p-4">
                    <Droplets className="w-6 h-6 mb-2 text-white/80" />
                    <p className="text-white/70 text-sm">{t('weather.metrics.humidity')}</p>
                    <p className="text-xl font-bold">{weather.humidity}%</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4">
                    <Wind className="w-6 h-6 mb-2 text-white/80" />
                    <p className="text-white/70 text-sm">{t('weather.metrics.wind')}</p>
                    <p className="text-xl font-bold">{weather.wind_speed} km/h</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4">
                    <Thermometer className="w-6 h-6 mb-2 text-white/80" />
                    <p className="text-white/70 text-sm">{t('weather.metrics.feelsLike')}</p>
                    <p className="text-xl font-bold">{weather.feels_like || weather.temperature}°C</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4">
                    <CloudRain className="w-6 h-6 mb-2 text-white/80" />
                    <p className="text-white/70 text-sm">{t('weather.metrics.rainChance')}</p>
                    <p className="text-xl font-bold">{weather.precipitation_probability || 0}%</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Spraying Advisory */}
          <Card className={`mb-6 border-2 ${weather.is_suitable_for_spraying ? 'border-farm-success bg-farm-success/5' : 'border-farm-warning bg-farm-warning/5'}`}>
            <CardContent className="p-4 flex items-center gap-4">
              {weather.is_suitable_for_spraying ? (
                <><CheckCircle className="w-8 h-8 text-farm-success" />
                  <div><h3 className="font-semibold text-farm-success">{t('weather.spraying.goodTitle')}</h3>
                    <p className="text-sm text-muted-foreground">{t('weather.spraying.goodBody')}</p>
                  </div></>
              ) : (
                <><AlertTriangle className="w-8 h-8 text-farm-warning" />
                  <div><h3 className="font-semibold text-farm-warning">{t('weather.spraying.badTitle')}</h3>
                    <p className="text-sm text-muted-foreground">{weather.spraying_advice || t('weather.spraying.badBody')}</p>
                  </div></>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* 7-Day Forecast */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-farm-primary" />
            {t('weather.forecast7Day')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {forecast.map((day, index) => (
              <div key={index} className="text-center p-4 bg-muted/50 rounded-xl">
                <p className="text-sm font-medium mb-2">
                  {new Date(day.date).toLocaleDateString(i18n.language, { weekday: 'short' })}
                </p>
                <div className="w-10 h-10 mx-auto text-farm-primary mb-2">
                  <WeatherIcon condition={day.condition} />
                </div>
                <p className="font-bold">{day.temp_max}°</p>
                <p className="text-sm text-muted-foreground">{day.temp_min}°</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
